import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut } from "lucide-react";
import axios from "axios";
import { NAV_SECTIONS } from "../data/content";
import { scrollToId } from "../lib/scroll";
import { useAuth } from "../lib/auth";
import { RoseMark } from "./common";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleMark = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.97 11.97 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
    />
  </svg>
);

const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);
  const { user, checking, setUser } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout non riuscito", e);
    } finally {
      setUser(null);
      setOpen(false);
    }
  };

  const dark = scrolled;

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-[80] transition-all duration-500 ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-md border-b border-stone/70 shadow-[0_8px_30px_rgba(43,35,31,0.08)]"
            : "bg-gradient-to-b from-ink/60 to-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[72px]">
          <button
            onClick={() => go("home")}
            data-testid="nav-logo-button"
            className="flex items-center gap-3 group"
            aria-label="Torna all'inizio"
          >
            <RoseMark
              className={`w-8 h-8 transition-colors duration-500 ${
                dark ? "text-gold" : "text-goldlight"
              } group-hover:rotate-45 transition-transform duration-700`}
            />
            <span
              className={`font-serif text-lg sm:text-xl font-semibold tracking-wide transition-colors duration-500 ${
                dark ? "text-espresso" : "text-ivory"
              }`}
            >
              Santa Caterina
              <span className="hidden sm:inline text-gold"> · Galatina</span>
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-7">
            <nav className="flex items-center gap-5" aria-label="Navigazione principale">
              {NAV_SECTIONS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => go(id)}
                  data-testid={`nav-link-${id}`}
                  className={`nav-link text-[13px] font-medium tracking-wide uppercase transition-colors duration-300 ${
                    active === id ? "active" : ""
                  } ${
                    dark
                      ? active === id
                        ? "text-espresso"
                        : "text-taupe hover:text-espresso"
                      : active === id
                        ? "text-ivory"
                        : "text-ivory/75 hover:text-ivory"
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>

            <span
              className={`w-px h-6 ${dark ? "bg-stone" : "bg-ivory/30"}`}
              aria-hidden="true"
            />

            {!checking && !user && (
              <button
                onClick={loginWithGoogle}
                data-testid="login-button"
                className={`flex items-center gap-2 text-[13px] font-semibold tracking-wide uppercase px-5 py-2.5 rounded-full transition-colors duration-300 ${
                  dark
                    ? "bg-espresso text-ivory hover:bg-gold hover:text-ink"
                    : "bg-ivory text-espresso hover:bg-goldlight"
                }`}
              >
                <GoogleMark />
                Accedi
              </button>
            )}

            {user && (
              <div className="flex items-center gap-3" data-testid="user-chip">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-9 h-9 rounded-full border-2 border-gold object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="w-9 h-9 rounded-full border-2 border-gold bg-espresso text-goldlight flex items-center justify-center font-serif font-semibold">
                    {(user.name || "U")[0].toUpperCase()}
                  </span>
                )}
                <span
                  className={`hidden xl:inline text-sm font-medium max-w-[140px] truncate ${
                    dark ? "text-espresso" : "text-ivory"
                  }`}
                >
                  {user.name}
                </span>
                <button
                  onClick={logout}
                  data-testid="logout-button"
                  aria-label="Esci"
                  title="Esci"
                  className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors duration-300 ${
                    dark
                      ? "text-taupe hover:text-terracotta hover:bg-sand"
                      : "text-ivory/80 hover:text-goldlight"
                  }`}
                >
                  <LogOut size={17} />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Apri il menu"
            className={`lg:hidden flex items-center justify-center w-11 h-11 transition-colors duration-300 ${
              dark ? "text-espresso" : "text-ivory"
            }`}
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] bg-ink flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 sm:px-8 h-[72px]">
              <div className="flex items-center gap-3">
                <RoseMark className="w-8 h-8 text-goldlight" />
                <span className="font-serif text-lg font-semibold text-ivory">
                  Santa Caterina <span className="text-gold">· Galatina</span>
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Chiudi il menu"
                className="flex items-center justify-center w-11 h-11 text-ivory hover:text-goldlight transition-colors"
              >
                <X size={26} />
              </button>
            </div>
            <nav
              className="flex-1 flex flex-col justify-center px-8 sm:px-14 gap-1 overflow-y-auto"
              aria-label="Menu mobile"
            >
              {NAV_SECTIONS.map(({ id, label }, i) => (
                <motion.button
                  key={id}
                  onClick={() => go(id)}
                  data-testid={`mobile-nav-link-${id}`}
                  className="group flex items-baseline gap-4 py-3 text-left border-b border-ivory/10"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className="font-mono text-xs text-gold">0{i + 1}</span>
                  <span className="font-serif text-3xl sm:text-4xl font-semibold text-ivory group-hover:text-goldlight transition-colors duration-300">
                    {label}
                  </span>
                </motion.button>
              ))}
              <motion.button
                onClick={() => go("fonti")}
                data-testid="mobile-nav-link-fonti"
                className="group flex items-baseline gap-4 py-3 text-left"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + NAV_SECTIONS.length * 0.06, duration: 0.5 }}
              >
                <span className="font-mono text-xs text-gold">
                  0{NAV_SECTIONS.length + 1}
                </span>
                <span className="font-serif text-3xl sm:text-4xl font-semibold text-ivory group-hover:text-goldlight transition-colors duration-300">
                  Fonti
                </span>
              </motion.button>
            </nav>

            <motion.div
              className="px-8 sm:px-14 pb-6"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              {!checking && !user && (
                <button
                  onClick={loginWithGoogle}
                  data-testid="mobile-login-button"
                  className="flex items-center gap-3 bg-ivory text-espresso font-semibold text-sm tracking-wide uppercase px-6 py-3.5 rounded-full hover:bg-goldlight transition-colors duration-300"
                >
                  <GoogleMark className="w-5 h-5" />
                  Accedi con Google
                </button>
              )}
              {user && (
                <div
                  className="flex items-center justify-between gap-4"
                  data-testid="mobile-user-chip"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-10 h-10 rounded-full border-2 border-gold object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="w-10 h-10 rounded-full border-2 border-gold bg-espresso text-goldlight flex items-center justify-center font-serif font-semibold">
                        {(user.name || "U")[0].toUpperCase()}
                      </span>
                    )}
                    <span className="text-ivory text-sm font-medium truncate">
                      {user.name}
                    </span>
                  </div>
                  <button
                    onClick={logout}
                    data-testid="mobile-logout-button"
                    className="flex items-center gap-2 text-ivory/70 hover:text-goldlight text-xs font-mono tracking-widest uppercase transition-colors"
                  >
                    <LogOut size={15} />
                    Esci
                  </button>
                </div>
              )}
            </motion.div>

            <div className="px-8 sm:px-14 pb-10">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-ivory/50">
                Galatina · Lecce · Puglia
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
